import React, { useEffect, useMemo } from 'react';
import 'twin.macro';
import { EnvelopeIcon as Mail } from '@heroicons/react/24/outline';
import InputComponent from '@teetsh/app/src/components/Form/InputGroup/formWrapper';
import Button from 'components/Buttons/Button';
import { Field, Form } from 'react-final-form';
import { Link, useNavigate } from 'react-router-dom';
import { composeValidators } from 'utils/formUtils';
import { useAuth } from 'containers/App/context/AuthContext';
import { guessTZ } from 'containers/timezones';
import { FORM_ERROR } from 'final-form';
import { RegisterToGoogleButton } from './components/RegisterToGoogleButton';
import { routes } from '@teetsh/app/src/containers/routes';
import { useInscriptionParams } from 'containers/Demarrage/useInscriptionParams';
import { usePostHog } from 'posthog-js/react';
import { Trans, useTranslation } from 'react-i18next';
import { useTranslatedValidation } from 'utils/useTranslatedValidation';
import { Parrain } from 'containers/Auth/LoginPage/Parrain';
import { MesssageWrapper } from 'components/Layouts/MessageWrapper';
import { UserLocale } from '@teetsh/app/src/types/user';
import { Checkbox } from 'components/Form/Checkboxes/Checkbox';
import { OnboardingWrapper } from 'containers/Demarrage';
import i18n from 'i18next';
import Select2 from 'components/Form/Select2';
import { getSortedLanguageOptions } from '@teetsh/app/src/utils/localization';

interface FormPayload {
  email: string;
  givenName: string;
  familyName: string;
  password: string;
  repassword: string;
  optin: boolean;
  location: string;
  countryCode: string;
  locale: UserLocale;
  zone?: string;
}

function Signup() {
  const navigate = useNavigate();
  const { signup, login } = useAuth();
  const {
    t,
    i18n: { language },
  } = useTranslation(['onboarding', 'common', 'countries']);
  const { required, isValidEmail } = useTranslatedValidation();

  const sortedLanguages = getSortedLanguageOptions();

  const currentLocale = language as UserLocale;
  // Memoizing initial locale to avoid the form to refresh/clear when changing the locale
  const initialLocale = useMemo(() => currentLocale, []);

  const {
    edtTemplateId,
    programmationTemplateId,
    sequenceTemplateId,
    edtTemplateDocumentId,
    programmationTemplateDocumentId,
    sequenceTemplateDocumentId,
    shareToken,
    shareEntityId,
    shareEntityType,
    parrainageToken,
    parrainName,
  } = useInscriptionParams();
  const posthog = usePostHog();
  useEffect(() => {
    posthog.capture('Signup page viewed');
  }, []);

  return (
    <OnboardingWrapper
      floating
      withLogoOnLargerScreens
      withLoginLink
      large={!!parrainName && !!parrainageToken}
    >
      <MesssageWrapper>
        {parrainName && parrainageToken && <Parrain parrain={parrainName} />}

        <>
          <h2 tw="display leading-none">{t('onboarding:signup.title')}</h2>
          <div tw="mt-6">
            <Form<FormPayload>
              initialValues={{
                location: guessTZ(),
                locale: initialLocale,
              }}
              mutators={{
                setField: ([field, value], state, { changeValue }) => {
                  changeValue(state, field, () => value);
                },
              }}
              validate={values => {
                const errors: any = {};
                if (values.repassword !== values.password) {
                  errors.repassword = t(
                    'onboarding:signup.noMatchingPasswords',
                  );
                }
                return errors;
              }}
              onSubmit={values =>
                signup({
                  email: values.email,
                  givenName: values.givenName,
                  familyName: values.familyName,
                  password: values.password,
                  optin: values.optin,
                  location: values.location,
                  countryCode:
                    values.countryCode === 'FR'
                      ? values.zone || 'FR'
                      : values.countryCode,
                  locale: values.locale,
                  parrainageToken,
                  edtTemplateId,
                  sequenceTemplateId,
                  programmationTemplateId,
                  edtTemplateDocumentId,
                  sequenceTemplateDocumentId,
                  programmationTemplateDocumentId,
                  shareToken,
                  shareEntityId,
                  shareEntityType,
                })
                  .then(() => login(values))
                  .then(() => navigate(routes.demarrage))
                  .catch(err => {
                    if (err?.response?.status === 409) {
                      return {
                        email: (
                          <div tw="text-sm">
                            {t('onboarding:signup.alreadyUsedEmail')}
                            <Link to="/login" tw="pl-1">
                              {t('onboarding:signup.pleaseSignin')}
                            </Link>
                            .
                          </div>
                        ),
                      };
                    }
                    return {
                      [FORM_ERROR]: t('common:messages.genericError'),
                    };
                  })
              }
            >
              {({
                handleSubmit,
                submitting,
                submitError,
                invalid,
                dirtyFieldsSinceLastSubmit,
              }) => (
                <form tw="mb-4" onSubmit={handleSubmit}>
                  <div tw="mb-8">
                    <div tw="flex flex-wrap">
                      <div tw="w-full md:w-1/2">
                        <Field
                          name="givenName"
                          large
                          label={t('onboarding:signup.firstName')}
                          id="givenName"
                          autoFocus
                          validate={required}
                          component={InputComponent}
                          tw="text-lg" // font-size must be at least 16px to prevent iOS from zooming
                          enterKeyHint="next"
                        />
                      </div>
                      <div tw="w-full md:w-1/2 pl-0 md:pl-4">
                        <Field
                          name="familyName"
                          large
                          label={t('onboarding:signup.lastName')}
                          id="familyName"
                          validate={required}
                          component={InputComponent}
                          tw="text-lg"
                          enterKeyHint="next"
                        />
                      </div>
                    </div>
                    <Field
                      name="email"
                      large
                      label={t('onboarding:signup.email')}
                      id="email"
                      validate={composeValidators(required, isValidEmail)}
                      icon={<Mail tw="h-5 w-5" />}
                      component={InputComponent}
                      type="email"
                      tw="text-lg"
                      enterKeyHint="next"
                    />
                    <div tw="flex flex-wrap">
                      <div tw="w-full md:w-1/2">
                        <Field
                          name="password"
                          type="password"
                          label={t('onboarding:signup.password')}
                          component={InputComponent}
                          large
                          validate={required}
                          id="register-form-password"
                          tw="text-lg"
                          enterKeyHint="next"
                        />
                      </div>
                      <div tw="w-full md:w-1/2 pl-0 md:pl-4">
                        <Field
                          name="repassword"
                          type="password"
                          component={InputComponent}
                          large
                          validate={required}
                          label={t('onboarding:signup.passwordConfirm')}
                          id="register-form-repassword"
                          tw="text-lg"
                          enterKeyHint="next"
                        />
                      </div>
                    </div>
                    <div tw="flex flex-wrap">
                      <div tw="mb-4 w-full">
                        <Field
                          name="locale"
                          validate={required}
                          render={({ input }) => (
                            <Select2
                              label={t('onboarding:signup.language')}
                              aria-label="Langue"
                              large
                              tw="bg-white"
                              options={sortedLanguages}
                              value={input.value}
                              onChange={v => {
                                i18n.changeLanguage(v);
                                input.onChange(v);
                              }}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div tw="mb-4 mt-4">
                      <Field
                        name="optin"
                        render={({ input }) => (
                          <Checkbox
                            id={input.name}
                            label={
                              <>
                                <Trans i18nKey="onboarding:signup.optin">
                                  A
                                  <span tw="text-sm text-muted font-medium pl-1">
                                    B
                                  </span>
                                </Trans>
                              </>
                            }
                            onChange={input.onChange}
                            checked={input.value}
                          />
                        )}
                      />
                    </div>
                    {submitError && (
                      <div tw="text-danger-moderate mb-3 font-medium">
                        {submitError}
                      </div>
                    )}
                  </div>
                  <div tw="flex justify-center space-x-2">
                    <Button
                      intent="primary"
                      type="submit"
                      loading={submitting}
                      disabled={
                        submitting ||
                        (invalid &&
                          !Object.keys(dirtyFieldsSinceLastSubmit).includes(
                            'email',
                          ))
                      }
                      large
                    >
                      {t('onboarding:signup.createAccount')}
                    </Button>
                  </div>
                </form>
              )}
            </Form>
            <p tw="text-center text-neutral-quiet">{t('common:or')}</p>
            <div tw="flex justify-center my-4">
              <RegisterToGoogleButton
                sequenceTemplateId={sequenceTemplateId}
                edtTemplateId={edtTemplateId}
                edtTemplateDocumentId={edtTemplateDocumentId}
                programmationTemplateId={programmationTemplateId}
                programmationTemplateDocumentId={
                  programmationTemplateDocumentId
                }
                parrainageToken={parrainageToken}
                shareToken={shareToken}
                shareEntityId={shareEntityId}
                shareEntityType={shareEntityType}
              />
            </div>
          </div>
        </>
      </MesssageWrapper>
    </OnboardingWrapper>
  );
}

export { Signup };
